import React, { useState } from 'react';
import { Trophy, Plus, Trash2, ChevronUp, ChevronDown, Pencil, AlertCircle, X, Check } from 'lucide-react';
import { Chess } from 'chess.js';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * Extract PGN header tag value
 */
function extractTag(pgn, tag) {
  const match = pgn.match(new RegExp(`\\[${tag}\\s+"(.+?)"\\]`));
  return match ? match[1] : '';
}

/**
 * Split raw text into individual PGN strings.
 * PGNs are separated by a blank line followed by a [ bracket (new game header).
 */
function splitPGNs(raw) {
  const trimmed = raw.trim();
  if (!trimmed) return [];

  // If text starts with [, split on double-newline before a new header
  if (trimmed.startsWith('[')) {
    return trimmed.split(/\n\n(?=\[)/).map((s) => s.trim()).filter(Boolean);
  }

  // If text is just moves (no headers), treat as single PGN
  return [trimmed];
}

/**
 * Validate a PGN string using chess.js
 * Returns { valid, error, moveCount }
 */
function validatePGN(pgn) {
  try {
    const chess = new Chess();
    chess.loadPgn(pgn);
    const moves = chess.history();
    if (moves.length === 0) {
      return { valid: false, error: 'No moves found in PGN' };
    }
    return { valid: true, error: null, moveCount: moves.length };
  } catch (err) {
    return { valid: false, error: err.message || 'Invalid PGN' };
  }
}

const FavouriteGamesEditor = ({ block, onChange, theme, hideStylePanels }) => {
  const content = block.content || {};
  const games = content.games || [];

  const defaultPgn = '[Event "?"]\n[White "Player 1"]\n[Black "Player 2"]\n[Result "*"]\n\n1. e4 e5 2. Nf3 Nc6 3. Bb5 *';
  const [pgnInput, setPgnInput] = useState(defaultPgn);
  const [showAddForm, setShowAddForm] = useState(false);
  const [parseErrors, setParseErrors] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const updateGames = (newGames) => {
    onChange({ games: newGames });
  };

  const handleAddGames = () => {
    const pgnStrings = splitPGNs(pgnInput);
    if (pgnStrings.length === 0) {
      setParseErrors([{ index: 0, error: 'Please paste a PGN' }]);
      return;
    }

    const errors = [];
    const newGames = [];

    pgnStrings.forEach((pgn, i) => {
      const result = validatePGN(pgn);
      if (result.valid) {
        const white = extractTag(pgn, 'White');
        const black = extractTag(pgn, 'Black');
        const event = extractTag(pgn, 'Event');
        newGames.push({
          id: `fg_${Date.now()}_${i}`,
          pgn,
          whiteName: white && white !== '?' ? white : '',
          blackName: black && black !== '?' ? black : '',
          orientation: 'white',
          label: event && event !== '?' ? event : '',
        });
      } else {
        errors.push({ index: i + 1, error: result.error });
      }
    });

    if (newGames.length > 0) {
      updateGames([...games, ...newGames]);
      setPgnInput(defaultPgn);
      if (errors.length === 0) {
        setShowAddForm(false);
      }
    }
    setParseErrors(errors);
  };

  const removeGame = (id) => {
    updateGames(games.filter((g) => g.id !== id));
    if (editingId === id) setEditingId(null);
  };

  const moveGame = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= games.length) return;
    const updated = [...games];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updateGames(updated);
  };

  const updateGame = (id, field, value) => {
    updateGames(games.map((g) => (g.id === id ? { ...g, [field]: value } : g)));
  };

  const getGameLabel = (game, index) => {
    if (game.label) return game.label;
    const w = game.whiteName || 'White';
    const b = game.blackName || 'Black';
    return `Game ${index + 1}: ${w} vs ${b}`;
  };

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#749555]/20 rounded-lg">
            <Trophy size={24} className="text-[#749555]" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-medium mb-1">Favourite Games</h4>
            <p className="text-sm text-gray-400">
              Add your favourite chess games. Visitors can replay them
              move by move on your website with an interactive board.
            </p>
          </div>
        </div>
      </div>

      {/* Title Input */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1.5">
          Section Title
        </label>
        <input
          type="text"
          value={content.title || 'My Favourite Games'}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="My Favourite Games"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#749555] transition-colors"
        />
      </div>

      {/* Theme Match Toggle */}
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          handleChange('matchThemeSquares', !content.matchThemeSquares);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleChange('matchThemeSquares', !content.matchThemeSquares);
          }
        }}
        className="flex items-center justify-between p-3 bg-[#1a1a2e] border border-gray-700 rounded-lg cursor-pointer hover:border-gray-600 transition-colors"
      >
        <div>
          <span className="text-sm font-medium text-gray-300">
            Match Theme Color
          </span>
          <p className="text-xs text-gray-500 mt-0.5">
            Use your website's accent color for the dark squares
          </p>
        </div>
        <div
          className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
            content.matchThemeSquares === true ? 'bg-[#749555]' : 'bg-gray-600'
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
              content.matchThemeSquares === true ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </div>
      </div>

      {/* Games List */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-300">
            Games ({games.length})
          </label>
        </div>

        {games.length === 0 && !showAddForm && (
          <div className="text-center py-6 text-gray-500 text-sm border border-dashed border-gray-700 rounded-lg">
            No games added yet. Click below to add one.
          </div>
        )}

        <div className="space-y-2">
          {games.map((game, index) => (
            <div
              key={game.id}
              className="bg-[#1a1a2e] border border-gray-700 rounded-lg overflow-hidden"
            >
              {/* Game header row */}
              <div className="flex items-center gap-2 p-3">
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveGame(index, -1)}
                    disabled={index === 0}
                    className="p-0.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move up"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    onClick={() => moveGame(index, 1)}
                    disabled={index === games.length - 1}
                    className="p-0.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Move down"
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>

                {/* Orientation dot */}
                <div
                  className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    game.orientation === 'black'
                      ? 'bg-gray-800 ring-1 ring-gray-500'
                      : 'bg-white ring-1 ring-gray-300'
                  }`}
                  title={`Board orientation: ${game.orientation}`}
                />

                {/* Label */}
                <span className="text-sm text-gray-200 flex-1 truncate">
                  {getGameLabel(game, index)}
                </span>

                {/* Edit / Delete */}
                <button
                  onClick={() => setEditingId(editingId === game.id ? null : game.id)}
                  className="p-1.5 text-gray-400 hover:text-[#749555] transition-colors"
                  title="Edit game details"
                >
                  {editingId === game.id ? <X size={14} /> : <Pencil size={14} />}
                </button>
                <button
                  onClick={() => removeGame(game.id)}
                  className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                  title="Remove game"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Inline edit form */}
              {editingId === game.id && (
                <div className="px-3 pb-3 space-y-3 border-t border-gray-700/50 pt-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Game Label</label>
                    <input
                      type="text"
                      value={game.label || ''}
                      onChange={(e) => updateGame(game.id, 'label', e.target.value)}
                      placeholder="e.g. World Championship 1972 Game 6"
                      className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-600 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#749555]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">White</label>
                      <input
                        type="text"
                        value={game.whiteName || ''}
                        onChange={(e) => updateGame(game.id, 'whiteName', e.target.value)}
                        placeholder="Player name"
                        className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-600 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#749555]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Black</label>
                      <input
                        type="text"
                        value={game.blackName || ''}
                        onChange={(e) => updateGame(game.id, 'blackName', e.target.value)}
                        placeholder="Player name"
                        className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-600 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#749555]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5">Board Orientation</label>
                    <div className="flex gap-2">
                      {['white', 'black'].map((color) => (
                        <button
                          key={color}
                          onClick={() => updateGame(game.id, 'orientation', color)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                            game.orientation === color
                              ? 'bg-[#749555] text-white'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          <div
                            className={`w-3 h-3 rounded-full ${
                              color === 'black'
                                ? 'bg-gray-900 ring-1 ring-gray-500'
                                : 'bg-white ring-1 ring-gray-300'
                            }`}
                          />
                          {color.charAt(0).toUpperCase() + color.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Add Game Section */}
      {showAddForm ? (
        <div className="space-y-3">
          <textarea
            value={pgnInput}
            onChange={(e) => {
              setPgnInput(e.target.value);
              setParseErrors([]);
            }}
            placeholder="Paste PGN here..."
            rows={8}
            className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white text-sm font-mono placeholder-gray-500 focus:outline-none focus:border-[#749555] transition-colors resize-y"
          />
          {/* Parse Errors */}
          {parseErrors.length > 0 && (
            <div className="space-y-1">
              {parseErrors.map((err, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-red-400">
                  <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
                  <span>
                    {parseErrors.length > 1 ? `PGN ${err.index}: ` : ''}
                    {err.error}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleAddGames}
              disabled={!pgnInput.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-[#749555] hover:bg-[#648B48] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Check size={14} />
              Add Game{splitPGNs(pgnInput).length > 1 ? 's' : ''}
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setPgnInput(defaultPgn);
                setParseErrors([]);
              }}
              className="px-4 py-2 text-gray-400 hover:text-white text-sm rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-700 hover:border-[#749555] text-gray-400 hover:text-[#749555] rounded-lg text-sm font-medium transition-colors"
        >
          <Plus size={16} />
          Add Game
        </button>
      )}

      {/* Style Panels */}
      {!hideStylePanels && (
        <>
          <SizeSpacingPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={BLOCK_FEATURES['favourite-games']}
          />
          <ColorsFontsPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={BLOCK_FEATURES['favourite-games']}
            theme={theme || {}}
            blockType="favourite-games"
          />
        </>
      )}
    </div>
  );
};

export default FavouriteGamesEditor;
